/**
 * CosmicNotificationContext - P5.5 Cosmic Notification UI
 * 
 * MindFlow bildirimleri için global context ve portal sistemi
 * 
 * Notification Types:
 * - info, success, warning, shield, streak, assist
 * 
 * Components:
 * - CosmicNotificationBanner (top-right)
 * - CosmicNotificationCard (bottom-center)
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Flame, Brain, Zap, Heart, Calendar, CheckCircle, AlertTriangle, Info, Sparkles } from 'lucide-react';
import { COSMIC_COLORS } from '../components/mindflow/cosmicTheme';

const CosmicNotificationContext = createContext(null);

// Type configurations
const NOTIFICATION_TYPES = {
  info: {
    icon: Info,
    gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    glowColor: 'rgba(99, 102, 241, 0.4)',
    borderColor: 'rgba(99, 102, 241, 0.3)',
  },
  success: {
    icon: CheckCircle,
    gradient: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
    glowColor: 'rgba(16, 185, 129, 0.4)',
    borderColor: 'rgba(16, 185, 129, 0.3)',
  },
  warning: {
    icon: AlertTriangle,
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
    glowColor: 'rgba(245, 158, 11, 0.4)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
  },
  shield: {
    icon: Shield,
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #06B6D4 100%)',
    glowColor: 'rgba(139, 92, 246, 0.4)',
    borderColor: 'rgba(139, 92, 246, 0.3)',
  },
  streak: {
    icon: Flame,
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
    glowColor: 'rgba(245, 158, 11, 0.4)',
    borderColor: 'rgba(245, 158, 11, 0.3)',
  },
  assist: {
    icon: Brain,
    gradient: 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)',
    glowColor: 'rgba(236, 72, 153, 0.4)',
    borderColor: 'rgba(236, 72, 153, 0.3)',
  },
};

// Banner Component
function CosmicNotificationBanner({ notification, onDismiss }) {
  const { id, type, title, description, duration = 6000, closable = true } = notification;
  const config = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.info;
  const IconComponent = config.icon;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => onDismiss(id), duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 100, y: -10 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      exit={{ opacity: 0, x: 100 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="relative flex items-start gap-3 p-4 rounded-2xl max-w-sm backdrop-blur-xl"
      style={{
        background: `linear-gradient(135deg, rgba(15, 11, 26, 0.95) 0%, rgba(30, 20, 50, 0.9) 100%)`,
        border: `1px solid ${config.borderColor}`,
        boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 40px ${config.glowColor}`,
      }}
    >
      {/* Icon */}
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: config.gradient }}
      >
        <IconComponent className="w-5 h-5 text-white" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white mb-0.5">{title}</p>
        {description && (
          <p className="text-xs text-white/60 line-clamp-2">{description}</p>
        )}
      </div>

      {/* Close Button */}
      {closable && (
        <button
          onClick={() => onDismiss(id)}
          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4 text-white/50" />
        </button>
      )}

      {/* Progress Bar */}
      {duration > 0 && (
        <motion.div
          initial={{ width: '100%' }}
          animate={{ width: '0%' }}
          transition={{ duration: duration / 1000, ease: 'linear' }}
          className="absolute bottom-0 left-0 h-0.5 rounded-full"
          style={{ background: config.gradient }}
        />
      )}
    </motion.div>
  );
}

// Card Component
function CosmicNotificationCard({ notification, onDismiss }) {
  const { id, type, title, subtitle, ctaLabel, onCtaClick, closable = true } = notification;
  const config = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.shield;
  const IconComponent = config.icon;

  const handleCtaClick = () => {
    onCtaClick?.();
    onDismiss(id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="relative p-6 rounded-2xl max-w-md w-full mx-4 backdrop-blur-xl"
      style={{
        background: `linear-gradient(135deg, rgba(15, 11, 26, 0.98) 0%, rgba(30, 20, 50, 0.95) 100%)`,
        border: `1px solid ${config.borderColor}`,
        boxShadow: `0 16px 64px rgba(0, 0, 0, 0.5), 0 0 60px ${config.glowColor}`,
      }}
    >
      {/* Glow effect */}
      <div
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-[80px] opacity-30 pointer-events-none"
        style={{ background: config.gradient }}
      />

      {/* Close Button */}
      {closable && (
        <button
          onClick={() => onDismiss(id)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5 text-white/50" />
        </button>
      )}

      <div className="relative">
        {/* Icon */}
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ background: config.gradient, boxShadow: `0 8px 32px ${config.glowColor}` }}
        >
          <IconComponent className="w-8 h-8 text-white" />
        </motion.div>

        {/* Title */}
        <h3 className="text-xl font-bold text-white text-center mb-2">{title}</h3>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-sm text-white/60 text-center mb-6">{subtitle}</p>
        )}

        {/* CTA Button */}
        {ctaLabel && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleCtaClick}
            className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
            style={{
              background: config.gradient,
              boxShadow: `0 4px 20px ${config.glowColor}`,
            }}
          >
            {ctaLabel}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

// Portal Component
function CosmicNotificationPortal({ banners, cards, onDismissBanner, onDismissCard }) {
  return (
    <>
      {/* Banner Stack - Top Right */}
      <div className="fixed top-4 right-4 z-[200] flex flex-col gap-3">
        <AnimatePresence mode="popLayout">
          {banners.slice(0, 3).map((banner) => (
            <CosmicNotificationBanner
              key={banner.id}
              notification={banner}
              onDismiss={onDismissBanner}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Card - Bottom Center */}
      <AnimatePresence>
        {cards.length > 0 && (
          <div className="fixed inset-0 z-[199] flex items-end justify-center pb-8 pointer-events-none">
            <div className="pointer-events-auto">
              <CosmicNotificationCard
                notification={cards[0]}
                onDismiss={onDismissCard}
              />
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

// Provider
export function CosmicNotificationProvider({ children }) {
  const [banners, setBanners] = useState([]);
  const [cards, setCards] = useState([]);

  const generateId = () => `notification_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const notifyBanner = useCallback((payload) => {
    const notification = {
      id: generateId(),
      duration: 6000,
      closable: true,
      ...payload,
    };
    setBanners((prev) => [...prev, notification]);
    return notification.id;
  }, []);

  const notifyCard = useCallback((payload) => {
    const notification = {
      id: generateId(),
      closable: true,
      ...payload,
    };
    setCards((prev) => [...prev, notification]);
    return notification.id;
  }, []);

  const dismissBanner = useCallback((id) => {
    setBanners((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const dismissCard = useCallback((id) => {
    setCards((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setBanners([]);
    setCards([]);
  }, []);

  return (
    <CosmicNotificationContext.Provider
      value={{
        banners,
        cards,
        notifyBanner,
        notifyCard,
        dismissBanner,
        dismissCard,
        clearAll,
      }}
    >
      {children}
      <CosmicNotificationPortal
        banners={banners}
        cards={cards}
        onDismissBanner={dismissBanner}
        onDismissCard={dismissCard}
      />
    </CosmicNotificationContext.Provider>
  );
}

// Hook
export function useCosmicNotifications() {
  const context = useContext(CosmicNotificationContext);
  if (!context) {
    throw new Error('useCosmicNotifications must be used within CosmicNotificationProvider');
  }
  return context;
}

export default CosmicNotificationContext;
